import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import { getOidcLogoutUrl } from '@buffer-mono/shared-utils/urls'
import {
  Button,
  CheckIcon,
  XIcon,
  EyeIcon,
  EyeOffIcon,
  Flex,
  Form,
  IconButton,
  Input,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import ConfirmPasswordModal from '../../shared/components/ConfirmPasswordModal'
import style from '../ProfileSettings.module.css'

export const ACCOUNT_UPDATE_PASSWORD = graphql(/* GraphQL */ `
  mutation AccountUpdatePassword($input: UpdatePasswordInput!) {
    updatePassword(input: $input) {
      ... on EmptySuccess {
        _empty
      }
      ... on InvalidInputError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

/**
 * `--color-text-disabled` is too dark in dark mode. On the backgrounds of these
 * two controls it gives about 3:1 contrast, below the 4.5:1 target for
 * secondary text. `--color-text-subtle` gives about 7:1 in dark mode and about
 * 5:1 in light mode.
 */
const READABLE_PLACEHOLDER_STYLE = {
  '--input-placeholder-color': 'var(--color-text-subtle)',
} as CSSProperties
const READABLE_DISABLED_LABEL_STYLE = {
  '--button-text-color': 'var(--color-text-subtle)',
} as CSSProperties

interface PasswordRule {
  rule: string
  isValid: (password: string) => boolean
}

// prettier-ignore
const PASSWORD_RULES: PasswordRule[] = [
  { rule: 'At least 8 characters long', isValid: (p: string) => p.length >= 8 },
  { rule: 'Contains at least 1 uppercase character', isValid: (p: string) => !!p.match(/[A-Z]/) },
  { rule: 'Contains at least 1 number or symbol', isValid: (p: string) => !!p.match(/[\d\W]/) },
]

const Password = (): JSX.Element => {
  const [updatePassword, { data, error, loading }] = useMutation(
    ACCOUNT_UPDATE_PASSWORD,
  )

  const [isMasked, setIsMasked] = useState(true)
  const [newPassword, setNewPassword] = useState('')
  const [currentPasswordError, setCurrentPasswordError] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const isValidPassword = PASSWORD_RULES.every((rule) =>
    rule.isValid(newPassword),
  )
  const isChangeDisabled = !newPassword || !isValidPassword

  function handleSubmit(password: string): void {
    void updatePassword({
      variables: {
        input: {
          password,
          newPassword,
        },
      },
    })
  }

  function handleModalOpen(): void {
    setCurrentPasswordError('') // Clear any previous errors
    setIsModalOpen(true)
  }

  // Handle password update response
  useEffect(() => {
    if (!data && !error) return

    // Clear any existing error state
    setCurrentPasswordError('')

    const result = data?.updatePassword

    if (result?.__typename === 'EmptySuccess') {
      toast.success(
        'Your password has been updated! You will be redirected to the login page.',
        { duration: 5_000 },
      )
      window.location.assign(getOidcLogoutUrl())
      return
    }

    if (result?.__typename === 'UnauthorizedError') {
      // Incorrect current password - keep dialog open and show inline error
      setCurrentPasswordError(result.message)
      return
    }

    // For all other errors, show toast and close modal
    const errorMessage =
      result?.__typename === 'InvalidInputError' ||
      result?.__typename === 'UnexpectedError'
        ? result.message
        : 'An error occurred while updating your password.'

    toast.error(errorMessage)
    setIsModalOpen(false)
  }, [data, error])

  return (
    <Form name="updatePasswordForm" className={style.fullWidth}>
      <Form.Field name="np" controlId="np" className={style.fullWidth}>
        <Form.Label htmlFor="np">Password</Form.Label>
        <Flex gap="xs" align="center" className={style.fullWidth}>
          <Form.Control>
            <Input
              ref={inputRef}
              id="np"
              className={style.flexInput}
              style={READABLE_PLACEHOLDER_STYLE}
              type={isMasked ? 'password' : 'text'}
              autoComplete="new-password"
              name="np"
              placeholder="New password"
              onChange={(event): void => setNewPassword(event.target.value)}
              aria-invalid={newPassword && !isValidPassword ? true : undefined}
              aria-describedby={
                newPassword ? 'password-validation-messages' : undefined
              }
              suffix={
                newPassword && (
                  <IconButton
                    label={isMasked ? 'Show password' : 'Hide password'}
                    size="small"
                    variant="tertiary"
                    onClick={(): void => {
                      setIsMasked(!isMasked)
                      inputRef.current?.focus()
                    }}
                  >
                    {isMasked ? (
                      <EyeIcon size="small" />
                    ) : (
                      <EyeOffIcon size="small" />
                    )}
                  </IconButton>
                )
              }
            />
          </Form.Control>
          <ConfirmPasswordModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            description="To confirm this password change, please enter your previous password."
            onConfirm={(password): void => {
              handleSubmit(password)
            }}
            error={currentPasswordError}
            isLoading={loading}
          >
            <Button
              size="medium"
              variant="secondary"
              disabled={isChangeDisabled}
              className={style.fixedWidthButton}
              style={
                isChangeDisabled ? READABLE_DISABLED_LABEL_STYLE : undefined
              }
              onClick={handleModalOpen}
            >
              Change Password
            </Button>
          </ConfirmPasswordModal>
        </Flex>
      </Form.Field>
      {newPassword && (
        <div
          className={style.passwordRules}
          id="password-validation-messages"
          aria-live="polite"
        >
          <Flex gap="xs" direction="column" align="start">
            {PASSWORD_RULES.map((rule) => (
              <Text
                key={rule.rule}
                color={rule.isValid(newPassword) ? 'success' : 'critical'}
                icon={
                  rule.isValid(newPassword) ? (
                    <CheckIcon size="small" color="success" />
                  ) : (
                    <XIcon size="small" color="critical" />
                  )
                }
              >
                {rule.rule}
              </Text>
            ))}
          </Flex>
        </div>
      )}
    </Form>
  )
}

export default Password
